import { useEffect, useState } from "react";
import { PlayCircleIcon } from "@heroicons/react/24/outline";

import type { OfferingMedia } from "./types";
import { getOfferingMediaKind, getOfferingMediaLabel } from "./offeringMedia";

type OfferingMediaThumbnailProps = {
  // Pre-sorted by the parent (getSortedOfferingMedia).
  media: OfferingMedia[];
  serviceName: string;
  onOpenVideo: () => void;
};

// Fill the full width of the media section at a fixed height; object-cover
// crops as needed so regular photos look like a hero image. The slot renders
// ~670x208 (≈3.2:1) on desktop, so source media is best authored at ~2x
// (1400x440) with the subject centered and away from the edges — object-cover
// crops to a narrower ratio on mobile.
const MEDIA_SIZE = "h-52 w-full";

export default function OfferingMediaThumbnail({
  media,
  serviceName,
  onOpenVideo,
}: OfferingMediaThumbnailProps) {
  const primary = media[0];
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    setPreviewFailed(false);
  }, [primary?.url]);

  if (!primary || previewFailed) return null;

  const kind = getOfferingMediaKind(primary);
  const label = getOfferingMediaLabel(primary, serviceName, 0);

  // Images render inline on the card — no modal.
  if (kind === "image") {
    return (
      <img
        src={primary.url}
        alt={label}
        className={`${MEDIA_SIZE} object-cover`}
        onError={() => setPreviewFailed(true)}
        data-testid="offering-media-thumbnail"
      />
    );
  }

  // Videos open in a modal player on click.
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOpenVideo();
      }}
      className={`group relative block ${MEDIA_SIZE} overflow-hidden p-0 text-evergreen transition focus:outline-none focus:ring-2 focus:ring-evergreen focus:ring-offset-2`}
      aria-label={`Play video for ${serviceName}`}
      data-testid="offering-media-thumbnail"
    >
      <video
        src={primary.url}
        aria-label={label}
        className="h-full w-full object-cover"
        muted
        playsInline
        preload="metadata"
        onError={() => setPreviewFailed(true)}
      />
      <span className="absolute inset-0 flex items-center justify-center text-white">
        <span className="rounded-full bg-black/50 p-0.5 shadow-sm">
          <PlayCircleIcon className="h-9 w-9 drop-shadow" />
        </span>
      </span>
    </button>
  );
}
